// MF-67 — Journal Templates list (Maestro-managed entry types). Standard row actions
// (preview / edit / delete-with-confirmation), sorted by title, filterable by category.
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { configPath } from '../paths'
import { useNavigate } from 'react-router-dom'
import { Button, MenuItem, TextField, Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import RowActions from '../components/RowActions'
import ConfirmDelete from '../components/ConfirmDelete'
import PreviewDialog, { PreviewSection } from '../components/PreviewDialog'
import ListSearch from '../components/ListSearch'
import ListTable, { Muted, Stacked } from '../components/ListTable'
import ColorBadge, { CodeTag, Points, TypeBadge } from '../components/ColorBadge'
import { fieldTypeLabel } from '../components/FieldInput'
import { inputSx } from '../components/formStyles'
import { useCategories, useSeveritiesMap, useTypes } from '../components/useLookups'
import { loadTemplates } from '../../../../store/actions/journalTemplatesActions'
import { deleteTemplate } from '../../../../services/journal/templates.service'

// A template's severity on the school's scale. Types not graded by severity show "Not applicable";
// graded ones without a severity yet (e.g. imported history) show "Not set".
const SeverityCell = ({ code, severities, applies }) => {
  if (!applies) return <span style={{ fontSize: 12, color: 'rgb(100, 116, 139)' }}>Not applicable</span>
  if (!code) return <span style={{ fontSize: 12, color: 'rgb(220, 38, 38)' }}>Not set</span>
  const r = severities.get(code)
  return r ? <ColorBadge label={r.label} color={r.color} size='sm' /> : <CodeTag code={code} />
}

const TemplatesList = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { list, loading, totalRecords } = useSelector((state) => state.journalTemplates.templates)
  const severities = useSeveritiesMap()
  const { byCode: typesByCode } = useTypes()
  const appliesTo = (tpl) => Boolean(typesByCode.get(tpl?.category?.type)?.usesSeverity)
  const { list: categories } = useCategories()
  const [toDelete, setToDelete] = useState(null)
  const [preview, setPreview] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [search, setSearch] = useState('')
  const [categoryId, setCategoryId] = useState('')

  const params = {
    page: page + 1,
    limit: rowsPerPage,
    ...(search ? { search } : {}),
    ...(categoryId ? { categoryId } : {})
  }
  const reload = () => dispatch(loadTemplates(params))

  useEffect(() => {
    dispatch(
      loadTemplates({
        page: page + 1,
        limit: rowsPerPage,
        ...(search ? { search } : {}),
        ...(categoryId ? { categoryId } : {})
      })
    )
  }, [dispatch, page, rowsPerPage, search, categoryId])

  const onConfirmDelete = async () => {
    setDeleting(true)
    try {
      await deleteTemplate(toDelete._id)
      setToDelete(null)
      reload()
    } finally {
      setDeleting(false)
    }
  }

  const columns = [
    {
      key: 'title',
      label: 'Template',
      render: (r) => <Stacked primary={r.title} secondary={r.description} />
    },
    { key: 'category', label: 'Category', width: 200, render: (r) => r.category?.name || <Muted>—</Muted> },
    {
      key: 'severity',
      label: 'Severity',
      width: 230,
      render: (r) => (
        <SeverityCell code={r.defaults?.severity} severities={severities} applies={appliesTo(r)} />
      )
    },
    {
      key: 'type',
      label: 'Type',
      width: 140,
      render: (r) => (r.category?.type ? <TypeBadge code={r.category.type} /> : <Muted>—</Muted>)
    },
    {
      key: 'points',
      label: 'Points',
      width: 100,
      align: 'center',
      render: (r) => <Points value={r.defaults?.points ?? 0} />
    },
    {
      key: 'fields',
      label: 'Fields',
      width: 90,
      align: 'center',
      render: (r) => (r.fields?.length ? r.fields.length : <Muted>0</Muted>)
    },
    {
      key: 'actions',
      label: 'Actions',
      width: 196,
      align: 'right',
      render: (r) => (
        <RowActions
          onPreview={() => setPreview(r)}
          onEdit={() => navigate(configPath('templates', `edit/${r._id}`))}
          onDelete={() => setToDelete(r)}
        />
      )
    }
  ]

  return (
    <>
      <div style={{ padding: '8px 8px 24px' }}>
        <ListSearch
          value={search}
          placeholder='Search templates…'
          onChange={(v) => {
            setSearch(v)
            setPage(0)
          }}
        >
          <TextField
            select
            size='small'
            value={categoryId}
            onChange={(e) => {
              setCategoryId(e.target.value)
              setPage(0)
            }}
            SelectProps={{
              displayEmpty: true,
              SelectDisplayProps: { 'aria-label': 'Filter by category', 'aria-labelledby': undefined }
            }}
            sx={{ ...inputSx, minWidth: 220 }}
          >
            <MenuItem value=''>All categories</MenuItem>
            {categories.map((c) => (
              <MenuItem key={c._id} value={c._id}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
          {!loading ? (
            <Typography style={{ fontSize: 13, color: 'rgb(100, 116, 139)' }}>
              {totalRecords} {totalRecords === 1 ? 'template' : 'templates'}
            </Typography>
          ) : null}
          <Button
            variant='contained'
            onClick={() => navigate(configPath('templates', 'add'))}
            startIcon={<FontAwesomeIcon icon={['fas', 'plus']} style={{ fontSize: 11 }} />}
          >
            New template
          </Button>
        </ListSearch>
        <ListTable
          columns={columns}
          rows={list}
          loading={loading}
          emptyText={search || categoryId ? 'No templates match the filters' : 'No templates yet'}
          pagination={{
            count: totalRecords,
            page,
            rowsPerPage,
            onPageChange: setPage,
            onRowsPerPageChange: (n) => {
              setRowsPerPage(n)
              setPage(0)
            }
          }}
        />
      </div>

      <ConfirmDelete
        open={Boolean(toDelete)}
        entity='template'
        name={toDelete?.title}
        loading={deleting}
        onCancel={() => setToDelete(null)}
        onConfirm={onConfirmDelete}
      />
      <PreviewDialog
        open={Boolean(preview)}
        title={preview?.title || 'Template'}
        onClose={() => setPreview(null)}
        header={
          preview ? (
            <>
              <TypeBadge code={preview.category?.type || 'neutral'} size='md' />
              <SeverityCell
                code={preview.defaults?.severity}
                severities={severities}
                applies={appliesTo(preview)}
              />
            </>
          ) : null
        }
        rows={[
          { label: 'Category', value: preview?.category?.name },
          { label: 'Points', value: preview ? <Points value={preview.defaults?.points ?? 0} /> : null },
          { label: 'Description', value: preview?.description, full: true }
        ]}
      >
        {preview ? (
          <PreviewSection title={`Fields (${preview.fields?.length || 0})`}>
            {preview.fields?.length ? (
              <div className='flex flex-col' style={{ gap: 8 }}>
                {preview.fields.map((f, i) => (
                  <div
                    key={f.id || i}
                    className='flex items-center justify-between'
                    style={{
                      gap: 12,
                      padding: '10px 12px',
                      borderRadius: 8,
                      background: 'rgb(248, 250, 252)'
                    }}
                  >
                    <span style={{ fontSize: 13, fontWeight: 500 }}>
                      {f.label}
                      {f.required ? (
                        <span style={{ color: 'rgb(220, 38, 38)', marginLeft: 2 }}>*</span>
                      ) : null}
                    </span>
                    <span style={{ fontSize: 12, color: 'rgb(100, 116, 139)' }}>
                      {fieldTypeLabel(f.type)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <Muted>No fields — entries only capture the overview.</Muted>
            )}
          </PreviewSection>
        ) : null}
      </PreviewDialog>
    </>
  )
}

export default TemplatesList
